/**
 * normalize.js — Tenant Config Normalizer
 *
 * PURPOSE:
 *   Bridges the schema-free BE tenant-config payload to the widget's
 *   expected shape. BE stores every nested value as a string and admins
 *   can use arbitrary key names — this module:
 *     1. Deep-merges raw BE data over DEFAULT_TENANT_CONFIG so partial
 *        responses still render.
 *     2. Coerces "true" / "false" strings on ui_features.* and features.*
 *        into real booleans.
 *     3. Maps theme key aliases (primary -> primary_color etc.) to the
 *        canonical names the widget components read.
 *     4. Normalizes empty-string URL fields to null.
 *
 * INPUT:  raw object from GET /api/v1/tenant-config/{id}/ -> .data
 * OUTPUT: object shaped exactly like DEFAULT_TENANT_CONFIG.
 *
 * SEE ALSO: widget/CONFIG_CONTRACT.md for the authoritative key list.
 */

import { DEFAULT_TENANT_CONFIG } from "./state.js";

const THEME_ALIASES = {
  primary: "primary_color",
  accent: "accent_color",
  text: "text_color",
  background: "background_color",
  font: "font_family",
  selected_chip: "selected_chip_color",
  // Admin sometimes stores the membership fallback under these keys.
  membership_card_image: "card_logo_url",
  default_image: "card_logo_url",
};

const URL_KEY_SUFFIXES = ["_url", "_image_url"];

function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function coerceBool(v, fallback = false) {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true") return true;
    if (s === "false" || s === "") return false;
  }
  if (v == null) return fallback;
  return Boolean(v);
}

function isUrlKey(key) {
  return URL_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));
}

function applyThemeAliases(rawTheme) {
  if (!isPlainObject(rawTheme)) return {};
  const out = {};
  // Pass 1: copy canonical keys (anything not in THEME_ALIASES).
  // Canonical always wins so payload-iteration order does not matter.
  for (const [k, v] of Object.entries(rawTheme)) {
    if (!THEME_ALIASES[k]) out[k] = v;
  }
  // Pass 2: fill from aliases only when canonical absent.
  for (const [k, v] of Object.entries(rawTheme)) {
    const canonical = THEME_ALIASES[k];
    if (canonical && out[canonical] === undefined) {
      out[canonical] = v;
    }
  }
  return out;
}

function normalizeTheme(rawTheme) {
  const aliased = applyThemeAliases(rawTheme);
  const out = {};
  for (const [k, v] of Object.entries(aliased)) {
    if (isUrlKey(k) && v === "") {
      out[k] = null;
    } else {
      out[k] = v;
    }
  }
  return out;
}

function normalizeFlagSection(raw, defaults) {
  if (!isPlainObject(raw)) return { ...defaults };
  const out = { ...defaults };
  for (const [k, v] of Object.entries(raw)) {
    const fallback = defaults[k] ?? false;
    out[k] = coerceBool(v, fallback);
  }
  return out;
}

function normalizeContact(raw) {
  if (!isPlainObject(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    out[k] = v === "" ? null : v;
  }
  return out;
}

// Same plain string passthrough as normalizeContact — commerce7.* values
// (currently just stripe_publishable_key) are opaque strings, not flags.
function normalizeCommerce7(raw) {
  if (!isPlainObject(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    out[k] = v === "" ? null : v;
  }
  return out;
}

// guided_flow_questions arrives from the BE as a JSON-encoded string (the
// store keeps every value as a string — arrays can't be stored natively).
// Parse it, then keep only well-formed { label, value } items. A plain array
// is still accepted for back-compat (variant fallback seeds one directly).
// Parse failure or bad shape yields [] so the variant fallback takes over.
function normalizeGuidedFlow(raw) {
  let arr = raw;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (s === "") return [];
    try {
      arr = JSON.parse(s);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr.filter(
    (item) =>
      isPlainObject(item) &&
      typeof item.label === "string" && item.label.trim() !== "" &&
      typeof item.value === "string" && item.value.trim() !== "",
  );
}

// `search_bar` has the same storage problem as guided_flow_questions — a nested
// object with an array in it cannot live in the BE's string-only store — so it
// arrives as ONE JSON-encoded string. Parse it, then keep only a usable shape:
// a mount.selector is REQUIRED (without somewhere to attach, the feature can do
// nothing), and suggestions are filtered to well-formed { text, category? }
// entries. Anything malformed yields null, which reads as "this winery has no
// search bar" — the variant fallback (variants/<slug>/getSearchBar) then applies.
export function normalizeSearchBar(raw) {
  let obj = raw;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (s === "") return null;
    try {
      obj = JSON.parse(s);
    } catch {
      return null;
    }
  }
  if (!isPlainObject(obj)) return null;

  const mount = isPlainObject(obj.mount) ? obj.mount : {};
  const selector = typeof mount.selector === "string" ? mount.selector.trim() : "";
  if (!selector) return null;

  const suggestions = (Array.isArray(obj.suggestions) ? obj.suggestions : [])
    .map((item) => (typeof item === "string" ? { text: item } : item))
    .filter((item) => isPlainObject(item) && typeof item.text === "string" && item.text.trim() !== "")
    .map((item) => ({
      text: item.text.trim(),
      category: typeof item.category === "string" && item.category.trim() !== ""
        ? item.category.trim()
        : null,
    }));

  return {
    mount: {
      selector,
      // Where to put the bar relative to `selector`: inside it (append/prepend),
      // or beside a child of it named by `reference` (before/after).
      position: ["append", "prepend", "before", "after"].includes(mount.position)
        ? mount.position
        : "append",
      reference: typeof mount.reference === "string" ? mount.reference.trim() : "",
      // The old widget waited 2s for the winery's own scripts to build the nav.
      // Kept configurable rather than hard-coded; we also retry (search-bar.js).
      delay_ms: Number.isFinite(Number(mount.delay_ms)) ? Number(mount.delay_ms) : 0,
    },
    placeholder: typeof obj.placeholder === "string" ? obj.placeholder : "",
    loading_text: typeof obj.loading_text === "string" ? obj.loading_text : "",
    // Optional override for the bundled "generating response" animation.
    loading_image_url:
      typeof obj.loading_image_url === "string" && obj.loading_image_url.trim() !== ""
        ? obj.loading_image_url.trim()
        : null,
    min_width: Number.isFinite(Number(obj.min_width)) ? Number(obj.min_width) : 1024,
    suggestions,
  };
}

export function normalizeTenantConfig(raw) {
  const data = isPlainObject(raw) ? raw : {};

  const theme = {
    ...DEFAULT_TENANT_CONFIG.theme,
    ...normalizeTheme(data.theme),
  };

  const contact = {
    ...DEFAULT_TENANT_CONFIG.contact,
    ...normalizeContact(data.contact),
  };

  const commerce7 = {
    ...DEFAULT_TENANT_CONFIG.commerce7,
    ...normalizeCommerce7(data.commerce7),
  };

  const ui_features = normalizeFlagSection(
    data.ui_features,
    DEFAULT_TENANT_CONFIG.ui_features,
  );

  const features = normalizeFlagSection(
    data.features,
    DEFAULT_TENANT_CONFIG.features,
  );

  const guided_flow_questions = normalizeGuidedFlow(data.guided_flow_questions);

  const search_bar = normalizeSearchBar(data.search_bar);

  return {
    ...DEFAULT_TENANT_CONFIG,
    ...data,
    // Backend-derived billing/access gate. Coerced explicitly because the
    // `...data` spread above copies top-level keys through untouched — only the
    // features/ui_features sections get coerceBool — so a string "false" would
    // arrive truthy and the gate would silently fail open. Defaults to enabled
    // when absent so an older backend never blanks a live widget.
    chatbot_enabled: coerceBool(data.chatbot_enabled, true),
    tenant_id: data.tenant_id ?? DEFAULT_TENANT_CONFIG.tenant_id,
    display_name: data.display_name ?? DEFAULT_TENANT_CONFIG.display_name,
    theme,
    contact,
    commerce7,
    ui_features,
    features,
    guided_flow_questions,
    search_bar,
  };
}
